import { useEffect } from 'react';
import { PageLayout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Globe, 
  Smartphone, 
  Code2, 
  Palette, 
  Search, 
  ShieldCheck,
  Zap,
  Cloud,
  Database,
  CheckCircle2
} from 'lucide-react';

const webServices = [
  {
    icon: Palette,
    title: 'Custom Web Design',
    description: 'Unique, brand-aligned designs that make your business stand out from the competition.'
  },
  {
    icon: Search,
    title: 'SEO Optimization',
    description: 'Built-in search engine optimization to help customers find you online.'
  },
  {
    icon: Zap,
    title: 'Performance Focused',
    description: 'Lightning-fast loading speeds for better user experience and higher conversions.'
  },
  {
    icon: ShieldCheck,
    title: 'Secure Hosting',
    description: 'SSL certificates and secure hosting included with every website package.'
  }
];

const appServices = [
  {
    icon: Smartphone,
    title: 'Native iOS & Android',
    description: 'Native apps built with Swift and Kotlin for optimal performance.'
  },
  {
    icon: Code2,
    title: 'Cross-Platform',
    description: 'React Native and Flutter solutions for cost-effective multi-platform deployment.'
  },
  {
    icon: Cloud,
    title: 'Cloud Integration',
    description: 'Seamless integration with cloud services, APIs, and backend systems.'
  },
  {
    icon: Database,
    title: 'Backend Development',
    description: 'Robust server-side solutions to power your mobile applications.'
  }
];

const process = [
  {
    step: '01',
    title: 'Discovery',
    description: 'We learn about your business, goals, and target audience to create the perfect strategy.'
  },
  {
    step: '02',
    title: 'Design',
    description: 'Our AI creates custom designs that align with your brand and appeal to your customers.'
  },
  {
    step: '03',
    title: 'Development',
    description: 'We build your website or app using the latest technologies for optimal performance.'
  },
  {
    step: '04',
    title: 'Launch',
    description: 'We deploy your project and provide ongoing support to ensure your success.'
  }
];

const ServicesPage = () => {
  useEffect(() => {
    document.title = 'Services | TryWeb';
  }, []);

  return (
    <PageLayout>
      {/* Hero */}
      <section className="relative py-24 hero-glow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">
              <span className="text-gradient">Our Services</span>
            </h1>
            <p className="text-xl text-muted-foreground">
              From stunning websites to powerful mobile applications, we deliver comprehensive digital solutions 
              that help your business thrive in the modern world.
            </p>
          </div>
        </div>
      </section>

      {/* App Development Section (now first - real apps) */}
      <section className="py-24 bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              {/* Layered promo composition */}
              <div className="relative h-[460px] max-w-[480px] mx-auto">
                {/* Ambient glow */}
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(45,212,191,0.18),transparent_60%),radial-gradient(circle_at_75%_70%,rgba(244,114,182,0.18),transparent_60%)] blur-2xl" aria-hidden="true" />

                {/* Stir promo (back, rotated left) */}
                <a
                  href="https://stir-it.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute top-0 left-0 w-[52%] aspect-[9/19] rounded-[2rem] overflow-hidden border-[8px] border-zinc-900 shadow-2xl rotate-[-6deg] hover:rotate-0 transition-transform duration-500"
                  data-testid="services-stir-promo"
                  aria-label="Stir app"
                >
                  <img src="/apps/stir-hero.jpg" alt="Stir — recipe import promo" className="w-full h-full object-cover" />
                </a>

                {/* Kisses promo (front, rotated right) */}
                <a
                  href="https://kisses-id.app/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute bottom-0 right-0 w-[52%] aspect-[9/19] rounded-[2rem] overflow-hidden border-[8px] border-zinc-900 shadow-2xl rotate-[6deg] hover:rotate-0 transition-transform duration-500"
                  data-testid="services-kisses-promo"
                  aria-label="Kisses app"
                >
                  <img src="/apps/kisses-hero.jpg" alt="Kisses — pet ID promo" className="w-full h-full object-cover" />
                </a>
              </div>
            </div>

            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/10 border border-secondary/20 mb-6">
                <Smartphone className="w-4 h-4 text-secondary" />
                <span className="text-sm font-medium text-secondary">App Development</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                Mobile apps that users actually love
              </h2>
              <p className="text-muted-foreground mb-6">
                We build, ship and maintain real mobile apps. Two of our own — <a href="https://stir-it.app" target="_blank" rel="noopener noreferrer" className="text-secondary font-medium hover:underline">Stir</a> and <a href="https://kisses-id.app/" target="_blank" rel="noopener noreferrer" className="text-secondary font-medium hover:underline">Kisses</a> — are live on the App Store and Google Play right now. Whether you need a native app or a cross-platform solution, we'll take you from concept to launch.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {appServices.map((service, index) => {
                  const Icon = service.icon;
                  return (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-secondary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-sm">{service.title}</h3>
                        <p className="text-xs text-muted-foreground mt-1">{service.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Web Development Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
                <Globe className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-primary">Web Development</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                Websites that convert visitors into customers
              </h2>
              <p className="text-muted-foreground mb-8">
                We create stunning, responsive websites that not only look beautiful but are optimized
                to drive conversions and grow your business. Every website is built with your target
                audience in mind.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {webServices.map((service, index) => {
                  const Icon = service.icon;
                  return (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-sm">{service.title}</h3>
                        <p className="text-xs text-muted-foreground mt-1">{service.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="relative order-first lg:order-last">
              <div className="rounded-2xl overflow-hidden border border-white/10">
                <img
                  src="https://images.unsplash.com/photo-1767817099805-d79e31fb968c?w=800&q=80"
                  alt="Web Development"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Our Process</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              A streamlined approach that delivers results fast without compromising on quality.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((item, index) => (
              <div key={index} className="relative">
                <div className="text-6xl font-bold text-primary/10 mb-4">{item.step}</div>
                <h3 className="font-semibold text-xl mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.description}</p>
                {index < process.length - 1 && (
                  <ArrowRight className="hidden lg:block absolute top-8 -right-4 w-8 h-8 text-primary/20" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-24 bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Technologies We Use</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              We leverage the latest and most reliable technologies to build your digital products.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-6">
            {['React', 'Next.js', 'Node.js', 'Python', 'Swift', 'Kotlin', 'Flutter', 'AWS', 'MongoDB', 'PostgreSQL', 'Firebase', 'Tailwind'].map((tech) => (
              <div key={tech} className="bg-card/50 border border-white/5 rounded-lg p-4 text-center card-hover">
                <span className="text-sm font-medium">{tech}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">Ready to Start Your Project?</h2>
          <p className="text-muted-foreground mb-8">
            Let's discuss your requirements and create something amazing together.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/pricing">
              <Button size="lg" className="glow-primary gap-2" data-testid="services-pricing-cta">
                View Pricing <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" data-testid="services-contact-cta">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default ServicesPage;
